<script setup lang="ts">
import {
  ArrowUploadIcon,
  AttachIcon,
  DismissIcon,
  DocumentIcon,
  DocumentPdfIcon,
} from '@blpde/nx-icons';
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  ref,
  useTemplateRef,
  watch,
  type StyleValue,
} from 'vue';
import { NxDropdown } from '../Dropdown';
import { NxIconButton } from '../IconButton';
import { i18nInjectionKey, type IconComponent } from '../internal';
import { formatFileSize, validateFiles, type RejectedFile } from './file-input-validation';
import { FONT_SIZE_LOOKUP, type InputHeight } from './internal';
import NxFakeInput from './NxFakeInput.vue';

type FileInputProps = {
  /** The unique identifier of the element in the DOM (label association). */
  id?: string;
  /** Allow more than one file. When `false` a new selection replaces the current one. */
  multiple?: boolean;
  /** Native-style accept filter, e.g. `'image/*,.pdf'`. */
  accept?: string;
  /** Maximum number of files (only meaningful with `multiple`). */
  maxFiles?: number;
  /** Maximum size in bytes per file. */
  maxSize?: number;
  /** Maximum size in bytes across all files. */
  maxTotalSize?: number;
  /** Visual form: single-line input field vs large drop area. Defaults to `'input'`. */
  variant?: 'input' | 'dropzone';
  /** Click-only selection, no drag & drop. */
  disableDrop?: boolean;
  /** Hint text; defaults to the i18n `common.file_input.placeholder(_multiple)` message. */
  placeholder?: string;
  /** Preview list below the field. Defaults to `true` for `dropzone`, `false` for `input`. */
  showPreview?: boolean;
  /** Preview as rows (`list`) or thumbnail tiles (`grid`). Defaults to `'grid'`. */
  layout?: 'list' | 'grid';
  /** Thumbnail size in the preview. Defaults to `'m'`. */
  previewSize?: 's' | 'm' | 'l';
  /** Show a human-readable size per file. Defaults to `true`. */
  showFileSize?: boolean;
  /** Show a per-file delete button. Defaults to `true`. */
  removable?: boolean;
  /** Show a "remove all" button below the preview. Defaults to `false`. */
  clearable?: boolean;
  /** Refuse files already selected (same name + size + lastModified). Defaults to `true`. */
  dedupe?: boolean;
  disabled?: boolean;
  /** Show the current files but allow no add/remove. */
  readonly?: boolean;
  required?: boolean;
  /** The height of the input variant. Defaults to `'s'`. */
  height?: InputHeight;
  /** Validation message shown below the field; also colors the border. */
  error?: string;
  /** Additional styling for the wrapper element if necessary. */
  wrapperStyle?: StyleValue;
  /** Icon shown as trailing action (input) / centered (dropzone). Defaults to the upload icon. */
  dropIcon?: IconComponent;
};

const props = withDefaults(defineProps<FileInputProps>(), {
  id: undefined,
  multiple: false,
  accept: undefined,
  maxFiles: undefined,
  maxSize: undefined,
  maxTotalSize: undefined,
  variant: 'input',
  disableDrop: false,
  placeholder: undefined,
  showPreview: undefined,
  layout: 'grid',
  previewSize: 'm',
  showFileSize: true,
  removable: true,
  clearable: false,
  dedupe: true,
  disabled: false,
  readonly: false,
  required: false,
  height: 's',
  error: undefined,
  wrapperStyle: undefined,
  dropIcon: undefined,
});

const model = defineModel<File[]>({ default: () => [] });

const emit = defineEmits<{
  add: [files: File[]];
  remove: [payload: { file: File; index: number }];
  clear: [];
  reject: [payload: RejectedFile];
  drop: [event: DragEvent];
  dragenter: [event: DragEvent];
  dragleave: [event: DragEvent];
}>();

defineOptions({ inheritAttrs: false });

const i18n = inject(i18nInjectionKey)!;

const inert = computed(() => props.disabled || props.readonly);
const showPreviewResolved = computed(() => props.showPreview ?? props.variant === 'dropzone');
const placeholderText = computed(
  () =>
    props.placeholder ??
    i18n.t(
      props.multiple ? 'common.file_input.placeholder_multiple' : 'common.file_input.placeholder'
    )
);
const uploadIcon = computed(() => props.dropIcon ?? ArrowUploadIcon);

// Summary line shown inside the field instead of the individual files.
const selectedText = computed(() =>
  model.value.length === 1
    ? i18n.t('common.file_input.selected_one')
    : i18n.t('common.file_input.selected_many', { count: model.value.length })
);

const inputIcons = computed(() => [
  {
    align: 'end' as const,
    component: uploadIcon.value,
    filled: true,
    color: inert.value ? 'var(--nxt-border-strong)' : 'var(--nxt-brand-bg)',
    onClick: inert.value ? undefined : openPicker,
    label: placeholderText.value,
  },
]);

// --- file-management dropdown ---------------------------------------------------

const isOpen = ref(false);
const highlightedIndex = ref(-1);
const fakeInputRef = useTemplateRef<InstanceType<typeof NxFakeInput>>('fake-input');
const dropdownRef = useTemplateRef<HTMLElement>('dropdown-panel');

/** Field / chevron click: manage existing files, or pick some when there are none. */
function onFieldClick(): void {
  if (inert.value) return;
  if (model.value.length === 0) {
    openPicker();
    return;
  }
  isOpen.value = !isOpen.value;
}

/** Keyboard navigation on the field, mirroring NxSelectInput: arrows move the
 *  highlight through the dropdown rows, Enter/Delete/Backspace removes the
 *  highlighted file, Tab closes. Escape is handled by the document listener. */
function onKeydown(event: KeyboardEvent): void {
  if (inert.value) return;

  if (!isOpen.value) {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        onFieldClick();
        break;
      case 'ArrowDown':
      case 'ArrowUp':
        if (model.value.length > 0) {
          event.preventDefault();
          isOpen.value = true;
        }
        break;
    }
    return;
  }

  const maxIndex = model.value.length - 1;
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      highlightedIndex.value = Math.min(highlightedIndex.value + 1, maxIndex);
      break;
    case 'ArrowUp':
      event.preventDefault();
      highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0);
      break;
    case 'Enter':
    case ' ':
    case 'Delete':
    case 'Backspace':
      event.preventDefault();
      if (props.removable && highlightedIndex.value >= 0) {
        removeFile(highlightedIndex.value);
      }
      break;
    case 'Tab':
      isOpen.value = false;
      break;
  }
}

function stopEscWhenOpen(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.stopImmediatePropagation();
    isOpen.value = false;
  }
}

function onPointerDownOutside(event: PointerEvent): void {
  const target = event.target as Node;
  const wrapper = fakeInputRef.value?.divElement;
  if (!wrapper?.contains(target) && !dropdownRef.value?.contains(target)) {
    isOpen.value = false;
  }
}

watch(isOpen, (open) => {
  if (open) {
    highlightedIndex.value = 0;
    document.addEventListener('keydown', stopEscWhenOpen, true);
    document.addEventListener('pointerdown', onPointerDownOutside, true);
  } else {
    highlightedIndex.value = -1;
    document.removeEventListener('keydown', stopEscWhenOpen, true);
    document.removeEventListener('pointerdown', onPointerDownOutside, true);
  }
});

// Keep the highlighted row visible while navigating with the arrow keys.
watch(highlightedIndex, () => {
  nextTick(() => {
    dropdownRef.value?.querySelector('.file-row.highlighted')?.scrollIntoView({ block: 'nearest' });
  });
});

// --- selection ---------------------------------------------------------------

const fileInputRef = useTemplateRef<HTMLInputElement>('file-input');

function openPicker(): void {
  if (inert.value) return;
  fileInputRef.value?.click();
}

function addFiles(candidates: File[]): void {
  const result = validateFiles(candidates, model.value, {
    multiple: props.multiple,
    accept: props.accept,
    maxFiles: props.maxFiles,
    maxSize: props.maxSize,
    maxTotalSize: props.maxTotalSize,
    dedupe: props.dedupe,
  });
  for (const rejectedFile of result.rejected) emit('reject', rejectedFile);
  if (result.accepted.length === 0) return;
  model.value = props.multiple ? [...model.value, ...result.accepted] : [...result.accepted];
  emit('add', result.accepted);
}

function onPickerChange(event: Event): void {
  const input = event.target as HTMLInputElement;
  addFiles(Array.from(input.files ?? []));
  // Reset so picking the same file again re-fires `change`.
  input.value = '';
}

function removeFile(index: number): void {
  if (inert.value) return;
  const file = model.value[index];
  if (!file) return;
  model.value = model.value.filter((_, i) => i !== index);
  emit('remove', { file, index });
  if (model.value.length === 0) isOpen.value = false;
  // Keep the keyboard highlight on a valid row after the list shrank.
  highlightedIndex.value = Math.min(highlightedIndex.value, model.value.length - 1);
}

function clearAll(): void {
  if (inert.value) return;
  model.value = [];
  emit('clear');
  isOpen.value = false;
}

// --- drag & drop ---------------------------------------------------------------

const dropEnabled = computed(() => !props.disableDrop && !inert.value);
const dragOver = ref(false);
// dragenter/dragleave also fire for child elements; a depth counter keeps
// `dragOver` stable until the pointer really leaves the drop target.
let dragDepth = 0;

function onDragEnter(event: DragEvent): void {
  if (!dropEnabled.value) return;
  dragDepth++;
  dragOver.value = true;
  emit('dragenter', event);
}

function onDragLeave(event: DragEvent): void {
  if (!dropEnabled.value) return;
  dragDepth = Math.max(0, dragDepth - 1);
  if (dragDepth === 0) dragOver.value = false;
  emit('dragleave', event);
}

function onDrop(event: DragEvent): void {
  if (!dropEnabled.value) return;
  dragDepth = 0;
  dragOver.value = false;
  emit('drop', event);
  addFiles(Array.from(event.dataTransfer?.files ?? []));
}

// --- thumbnails ---------------------------------------------------------------

// Object-URL per image file; revoked when the file leaves the model or the
// component unmounts (missing revokes leak memory).
const objectUrls = new Map<File, string>();

function previewUrl(file: File): string | null {
  if (!file.type.startsWith('image/')) return null;
  let url = objectUrls.get(file);
  if (!url) {
    url = URL.createObjectURL(file);
    objectUrls.set(file, url);
  }
  return url;
}

// `deep` so cleanup also runs when a consumer mutates the bound array in place
// (push/splice) instead of reassigning it — otherwise removed images' object
// URLs would leak until unmount.
watch(
  model,
  (files) => {
    for (const [file, url] of objectUrls) {
      if (!files.includes(file)) {
        URL.revokeObjectURL(url);
        objectUrls.delete(file);
      }
    }
  },
  { deep: true }
);

onBeforeUnmount(() => {
  for (const url of objectUrls.values()) URL.revokeObjectURL(url);
  objectUrls.clear();
  document.removeEventListener('keydown', stopEscWhenOpen, true);
  document.removeEventListener('pointerdown', onPointerDownOutside, true);
});

function typeIcon(file: File): IconComponent {
  const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  return isPdf ? DocumentPdfIcon : DocumentIcon;
}

function fileKey(file: File, index: number): string {
  return `${index}|${file.name}|${file.size}|${file.lastModified}`;
}
</script>

<template>
  <div class="nx-file-input" :class="{ 'has-error': !!props.error }">
    <input
      ref="file-input"
      type="file"
      hidden
      :accept="props.accept"
      :multiple="props.multiple"
      :required="props.required"
      :disabled="inert"
      @change="onPickerChange"
    />

    <!-- input variant: single-line field like every other Nx input. The field
         shows a selection summary; the files are managed in a dropdown below.

         `:disabled="inert"` tells NxInputWrapper the state it drives the icon
         treatment from — `inert`, not `disabled`, because readonly also strips the
         trailing icon's handler and an icon that does nothing must not keep the
         interactive colour. The field's own fill stays tied to `props.disabled`
         via `data-disabled`: readonly is not disabled. -->
    <NxDropdown v-if="props.variant === 'input'" :is-open="isOpen" @close="isOpen = false">
      <template #trigger>
        <NxFakeInput
          v-bind="$attrs"
          :id="props.id"
          ref="fake-input"
          :wrapper-style="props.wrapperStyle"
          :height="props.height"
          :icons="inputIcons"
          class="file-field"
          :class="{ 'drag-over': dragOver }"
          :data-disabled="props.disabled ? true : undefined"
          :disabled="inert"
          role="button"
          :aria-expanded="isOpen"
          :tabindex="inert ? -1 : 0"
          @click="onFieldClick"
          @keydown="onKeydown"
          @dragover.prevent
          @dragenter.prevent="onDragEnter"
          @dragleave="onDragLeave"
          @drop.prevent="onDrop"
        >
          <span v-if="model.length === 0" class="summary placeholder">
            <slot name="empty">{{ placeholderText }}</slot>
          </span>
          <span v-else class="summary">
            <AttachIcon filled />
            {{ selectedText }}
          </span>
        </NxFakeInput>
      </template>

      <div
        ref="dropdown-panel"
        class="file-dropdown"
        :style="`--font-size: ${FONT_SIZE_LOOKUP[props.height]}`"
      >
        <div
          v-for="(file, index) in model"
          :key="fileKey(file, index)"
          class="file-row"
          :class="{ highlighted: highlightedIndex === index }"
          @pointerenter="highlightedIndex = index"
        >
          <span class="row-thumb">
            <slot name="preview" :file="file" :url="previewUrl(file)">
              <img v-if="previewUrl(file)" :src="previewUrl(file)!" alt="" />
              <component
                :is="typeIcon(file)"
                v-else
                class="nxt-icon-md"
                color="var(--nxt-fg-subtle)"
              />
            </slot>
          </span>
          <span class="row-name" :title="file.name">{{ file.name }}</span>
          <span v-if="props.showFileSize" class="row-size">{{ formatFileSize(file.size) }}</span>
          <button
            v-if="props.removable && !inert"
            type="button"
            class="row-remove"
            :class="{ highlighted: highlightedIndex === index }"
            :aria-label="i18n.t('common.file_input.remove')"
            @click.stop="removeFile(index)"
          >
            <DismissIcon class="nxt-icon-md" />
          </button>
        </div>
      </div>
    </NxDropdown>

    <!-- dropzone variant: large bordered drop area -->
    <div
      v-else
      v-bind="$attrs"
      :id="props.id"
      class="dropzone"
      :class="{ 'drag-over': dragOver, disabled: props.disabled }"
      :style="props.wrapperStyle"
      role="button"
      :tabindex="inert ? -1 : 0"
      @click="openPicker"
      @keydown.enter.prevent="openPicker"
      @keydown.space.prevent="openPicker"
      @dragover.prevent
      @dragenter.prevent="onDragEnter"
      @dragleave="onDragLeave"
      @drop.prevent="onDrop"
    >
      <slot>
        <component :is="uploadIcon" class="nxt-icon-xl" color="var(--nxt-fg-subtle)" />
        <span class="dropzone-hint">{{ placeholderText }}</span>
        <span class="dropzone-browse">{{ i18n.t('common.file_input.browse') }}</span>
      </slot>

      <!-- preview inside the drop area (dropzone variant). Clicks/keys are kept
           local so interacting with a file doesn't re-open the picker. -->
      <ul
        v-if="showPreviewResolved && model.length > 0"
        class="preview"
        :class="[props.layout, `preview-${props.previewSize}`]"
        @click.stop
        @keydown.stop
      >
        <li v-for="(file, index) in model" :key="fileKey(file, index)" class="preview-item">
          <slot name="file" :file="file" :index="index" :remove="() => removeFile(index)">
            <span class="thumb">
              <slot name="preview" :file="file" :url="previewUrl(file)">
                <img v-if="previewUrl(file)" :src="previewUrl(file)!" alt="" />
                <component
                  :is="typeIcon(file)"
                  v-else
                  class="nxt-icon-xl"
                  color="var(--nxt-fg-subtle)"
                />
              </slot>
            </span>
            <span class="meta">
              <span class="name" :title="file.name">{{ file.name }}</span>
              <span v-if="props.showFileSize" class="size">{{ formatFileSize(file.size) }}</span>
            </span>
            <NxIconButton
              v-if="props.removable && !inert"
              danger
              height="s"
              class="preview-remove"
              :aria-label="i18n.t('common.file_input.remove')"
              @click.stop="removeFile(index)"
            >
              <DismissIcon class="nxt-icon-md" />
            </NxIconButton>
          </slot>
        </li>
      </ul>
    </div>

    <!-- preview below the field (input variant); the dropzone renders it inside
         the drop area instead -->
    <ul
      v-if="props.variant === 'input' && showPreviewResolved && model.length > 0"
      class="preview"
      :class="[props.layout, `preview-${props.previewSize}`]"
    >
      <li v-for="(file, index) in model" :key="fileKey(file, index)" class="preview-item">
        <slot name="file" :file="file" :index="index" :remove="() => removeFile(index)">
          <span class="thumb">
            <slot name="preview" :file="file" :url="previewUrl(file)">
              <img v-if="previewUrl(file)" :src="previewUrl(file)!" alt="" />
              <component
                :is="typeIcon(file)"
                v-else
                class="nxt-icon-xl"
                color="var(--nxt-fg-subtle)"
              />
            </slot>
          </span>
          <span class="meta">
            <span class="name" :title="file.name">{{ file.name }}</span>
            <span v-if="props.showFileSize" class="size">{{ formatFileSize(file.size) }}</span>
          </span>
          <NxIconButton
            v-if="props.removable && !inert"
            danger
            height="s"
            class="preview-remove"
            :aria-label="i18n.t('common.file_input.remove')"
            @click.stop="removeFile(index)"
          >
            <DismissIcon class="nxt-icon-md" />
          </NxIconButton>
        </slot>
      </li>
    </ul>

    <button
      v-if="props.clearable && model.length > 1 && !inert"
      type="button"
      class="clear-all"
      @click="clearAll"
    >
      {{ i18n.t('common.file_input.clear') }}
    </button>

    <span v-if="props.error" class="error-text">{{ props.error }}</span>
  </div>
</template>

<style scoped src="./style.css" />
<style scoped>
.nx-file-input {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  width: 100%;
}

/* --- input variant ---------------------------------------------------------- */

/* The `file-field`/`drag-over` classes fall through onto the `.fake-input`
   div itself (which carries NxFakeInput's scope id, not ours), so the rules
   must be anchored via the component root + :deep — a bare `.file-field`
   selector never matches. */
.nx-file-input :deep(.fake-input.file-field) {
  display: flex;
  align-items: center;
  white-space: nowrap;
  cursor: pointer;
}

/* Same drop indicator as the dropzone variant: dashed brand border + tint. */
.nx-file-input :deep(.fake-input.drag-over) {
  border-color: var(--nxt-brand-bg);
  border-style: dashed;
  background-color: var(--nxt-brand-subtle-bg);
}

.summary {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

.summary.placeholder,
.placeholder {
  color: var(--nxt-fg-subtle);
  pointer-events: none;
}

.summary:not(.placeholder) {
  display: flex;
  align-items: center;
  gap: 0.5ch;
}

/* --- file-management dropdown (mirrors .select-dropdown in NxSelectInput) ---- */

.file-dropdown {
  font-size: var(--font-size);
  border-radius: var(--nxt-radius-md);
  padding: var(--nxt-space-2xs);
  background-color: var(--nxt-surface-raised-bg);
  border: solid var(--nxt-hairline) var(--nxt-border);
  box-shadow: var(--nxt-shadow-md);
  display: flex;
  flex-direction: column;
  max-height: var(--nxt-area-size-lg);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--nxt-fg-subtle) transparent;
}

.file-row.highlighted {
  background-color: var(--nxt-brand-subtle-bg);
}

.file-row {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  font-size: 0.875em;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  border-radius: var(--nxt-radius-sm);
  min-width: 0;
}

.row-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75em;
  height: 1.75em;
  overflow: hidden;
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-surface-sunken-bg);
  flex: 0 0 auto;
}

.row-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.row-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row-size {
  flex: 0 0 auto;
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-xs);
}

/* Mirrors the NxIconButton `danger` look (ghost + danger-subtle hover). */
.row-remove {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  border: none;
  padding: 0.125em;
  background: none;
  color: var(--nxt-fg-subtle);
  cursor: pointer;
  border-radius: var(--nxt-radius-sm);
  transition:
    background-color 0.12s,
    color 0.12s;
}

.row-remove:hover,
.row-remove.highlighted {
  color: var(--nxt-danger-bg);
  background-color: var(--nxt-danger-subtle-bg);
}

/* Keyboard highlight needs to read as focus, not just hover. */
.row-remove.highlighted {
  outline: var(--nxt-hairline) solid var(--nxt-danger-bg);
}

.row-remove:active {
  color: var(--nxt-danger-bg);
  background-color: var(--nxt-danger-bg-active);
}

/* --- dropzone variant --------------------------------------------------------- */

.dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--nxt-space-2xs);
  padding: var(--nxt-space-lg) var(--nxt-space-md);
  border: var(--nxt-hairline) dashed var(--nxt-border-strong);
  border-radius: var(--nxt-radius-md);
  color: var(--nxt-fg-subtle);
  cursor: pointer;
  transition:
    border-color 0.12s,
    background-color 0.12s;
}

.dropzone:hover {
  border-color: var(--nxt-brand-bg);
}
.dropzone:focus-visible {
  outline: 0;
  border-color: var(--nxt-input-border-focus);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-input-ring);
}

.dropzone.drag-over {
  border-color: var(--nxt-brand-bg);
  background-color: var(--nxt-brand-subtle-bg);
}

.dropzone.disabled {
  pointer-events: none;
  opacity: 0.6;
}

.dropzone-hint {
  font-size: var(--nxt-font-size-sm);
}

.dropzone-browse {
  font-size: var(--nxt-font-size-xs);
}

.has-error .dropzone,
.has-error :deep(.fake-input) {
  border-color: var(--nxt-danger-bg);
}

/* --- preview ---------------------------------------------------------------- */

.preview {
  display: flex;
  margin: 0;
  padding: 0;
  list-style: none;
  gap: var(--nxt-space-xs);
}

.preview.grid {
  flex-wrap: wrap;
}

.preview.list {
  flex-direction: column;
}

/* Inside the dropzone the preview sits within the drop area: fill the content
   width so items wrap (making the zone taller, never wider) and stay centered. */
.dropzone .preview {
  width: 100%;
  margin-top: var(--nxt-space-xs);
}

.dropzone .preview.grid {
  justify-content: center;
}

.dropzone .preview.list {
  align-items: center;
}

.preview-item {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  padding: var(--nxt-space-2xs);
  min-width: 0;
}

.preview.grid .preview-item {
  position: relative;
  flex-direction: column;
  align-items: stretch;
}

.thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-surface-sunken-bg);
  flex: 0 0 auto;
}

.preview-s .thumb {
  width: 3em;
  height: 3em;
}

.preview-m .thumb {
  width: 4em;
  height: 4em;
}

.preview-l .thumb {
  width: 6em;
  height: 6em;
}

.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.preview.grid .meta {
  max-width: 8em;
}

.name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--nxt-font-size-xs);
}

.size {
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-xs);
}

/* The remove control is an NxIconButton (ghost + danger red); only its corner
   placement in a grid tile is ours (:deep pierces the button's own scope). */
.preview.grid :deep(.preview-remove) {
  position: absolute;
  top: 0.125em;
  right: 0.125em;
}

.clear-all {
  align-self: flex-start;
  border: none;
  background: none;
  padding: 0;
  color: var(--nxt-brand-bg);
  font-size: var(--nxt-font-size-xs);
  cursor: pointer;
}

.clear-all:hover {
  text-decoration: underline;
}

.error-text {
  color: var(--nxt-danger-bg);
  font-size: var(--nxt-font-size-xs);
}
</style>
